import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Barcode, Download, FileSpreadsheet, PackageSearch, Printer, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, SHIPMENT_STATUS, API } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

export default function Shipments() {
    const { user } = useAuth();
    const [shipments, setShipments] = useState(null);
    const [detail, setDetail] = useState(null);
    const [scanCode, setScanCode] = useState("");
    const [scanResult, setScanResult] = useState(null);
    const [received, setReceived] = useState({});
    const [busy, setBusy] = useState(false);
    const isBuyer = user?.role === "buyer";

    const load = () => api.get("/shipments").then((r) => setShipments(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openBarcodeList = async (s) => {
        try {
            const r = await api.get(`/shipments/${s.shipment_id}/barcode-list`);
            setDetail(r.data);
            setScanResult(null);
            setReceived(Object.fromEntries((r.data.items || []).map((i) => [i.product_id, i.received_qty ?? i.ordered_qty])));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Liste alınamadı");
        }
    };

    const scan = async () => {
        if (!scanCode.trim()) return;
        try {
            const r = await api.post(`/shipments/${detail.shipment_id}/scan`, { barcode: scanCode.trim() });
            setScanResult({ ok: true, data: r.data });
        } catch (e) {
            setScanResult({ ok: false, msg: e.response?.data?.detail || "Barkod bulunamadı" });
        }
    };

    const downloadPdf = (d) => {
        const w = window.open("", "_blank");
        if (!w) return toast.error("Açılır pencere engellendi");
        w.document.write(`<html><head><title>Barkodlu Liste ${d.order_no}</title><style>body{font-family:sans-serif;padding:24px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #999;padding:6px;text-align:left;font-size:12px}h1{font-size:18px}</style></head><body><h1>Barkodlu Ürün Listesi — ${d.order_no}</h1><p>${d.supplier_name} → ${d.buyer_company_name}</p><table><tr><th>Barkod</th><th>Ürün</th><th>Birim</th><th>Sipariş</th><th>Sevk</th><th>Teslim</th></tr>${d.items.map((i) => `<tr><td>${i.barcode || "—"}</td><td>${i.product_name}</td><td>${i.unit}</td><td>${fmtQty4(i.ordered_qty, i.unit)}</td><td>${fmtQty4(i.shipped_qty, i.unit)}</td><td>${i.received_qty != null ? fmtQty4(i.received_qty, i.unit) : "—"}</td></tr>`).join("")}</table></body></html>`);
        w.document.close();
        w.print();
    };

    const confirmReceive = async () => {
        setBusy(true);
        try {
            const items = detail.items.map((i) => ({ product_id: i.product_id, received_qty: Number(received[i.product_id] ?? i.ordered_qty) }));
            const r = await api.post(`/shipments/${detail.shipment_id}/receive`, { items });
            if (r.data.discrepancies.length) {
                toast.warning(`Miktar farkı tespit edildi: ${r.data.discrepancies.join(", ")}`);
            } else {
                toast.success("Teslim alma kaydedildi — tüm miktarlar eşleşti");
            }
            openBarcodeList(detail);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="shipments-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Sevkiyat & Teslimat</h1>
                <p className="mt-1 text-sm text-muted-foreground">Tüm kargo hareketleriniz</p>
            </div>

            {!shipments ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="shipments-table">
                            <thead>
                                <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Sipariş</th>
                                    <th className="p-4">Tedarikçi</th>
                                    <th className="p-4">Kargo</th>
                                    <th className="p-4">Takip No</th>
                                    <th className="p-4">Durum</th>
                                    <th className="p-4">Tahmini Teslim</th>
                                    <th className="p-4">Teslim Alan</th>
                                    <th className="p-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shipments.map((s) => (
                                    <tr key={s.shipment_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`shipment-row-${s.shipment_id}`}>
                                        <td className="p-4"><Link to={`/panel/siparis/${s.order_id}`} className="font-semibold text-primary hover:underline">{s.order_no}</Link></td>
                                        <td className="p-4">{s.supplier_name}</td>
                                        <td className="p-4">{s.carrier}</td>
                                        <td className="p-4 font-mono text-xs font-semibold">{s.tracking_no}</td>
                                        <td className="p-4"><Badge variant="outline" className={`text-[10px] ${SHIPMENT_STATUS[s.status]?.cls}`} data-testid={`shipment-status-${s.shipment_id}`}>{SHIPMENT_STATUS[s.status]?.label}</Badge></td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(s.estimated_delivery)}</td>
                                        <td className="p-4 text-muted-foreground">{s.receiver || "—"}</td>
                                        <td className="p-4 text-right">
                                            <Button size="sm" variant="outline" onClick={() => openBarcodeList(s)} data-testid={`barcode-list-${s.shipment_id}`}>
                                                <Barcode className="mr-1.5 h-3.5 w-3.5" /> Barkodlu Liste
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {!shipments.length && (
                                    <tr><td colSpan={8} className="p-16 text-center text-sm text-muted-foreground" data-testid="shipments-empty">
                                        <PackageSearch className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Sevkiyat bulunmuyor.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
                <DialogContent className="max-w-3xl" data-testid="barcode-list-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">Barkodlu Ürün Listesi — {detail.order_no}</DialogTitle>
                            </DialogHeader>
                            <div className="flex flex-wrap gap-2">
                                <a href={`${API}/shipments/${detail.shipment_id}/barcode-list.xlsx`} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted" data-testid="barcode-list-excel">
                                    <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
                                </a>
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => window.print()} data-testid="barcode-list-print">
                                    <Printer className="mr-1 h-3.5 w-3.5" /> Yazdır
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadPdf(detail)} data-testid="barcode-list-pdf">
                                    <Download className="mr-1 h-3.5 w-3.5" /> PDF
                                </Button>
                            </div>
                            <div className="overflow-x-auto rounded-lg border" data-testid="barcode-list-table">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b bg-muted/60 text-left">
                                            <th className="p-2">Barkod</th><th className="p-2">Ürün</th><th className="p-2">Birim</th>
                                            <th className="p-2">Sipariş</th><th className="p-2">Sevk</th>
                                            <th className="p-2">{isBuyer && detail.status === "shipped" ? "Teslim Alınan *" : "Teslim Alınan"}</th>
                                            <th className="p-2">Fark</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.items.map((i) => {
                                            const rcv = isBuyer && detail.status === "shipped" ? Number(received[i.product_id] ?? i.ordered_qty) : i.received_qty;
                                            const variance = rcv != null ? Math.round((rcv - i.ordered_qty) * 1000) / 1000 : null;
                                            return (
                                                <tr key={i.product_id} className={`border-b last:border-0 ${variance ? "bg-amber-50" : ""}`} data-testid={`barcode-row-${i.product_id}`}>
                                                    <td className="p-2 font-mono font-semibold">{i.barcode || "—"}</td>
                                                    <td className="p-2">{i.product_name}{i.quality ? <span className="text-muted-foreground"> — {i.quality}</span> : null}</td>
                                                    <td className="p-2">{i.unit}</td>
                                                    <td className="p-2">{fmtQty4(i.ordered_qty, i.unit)}</td>
                                                    <td className="p-2">{fmtQty4(i.shipped_qty, i.unit)}</td>
                                                    <td className="p-2">
                                                        {isBuyer && detail.status === "shipped" ? (
                                                            <Input type="number" min="0" step="0.001" className="h-7 w-20 text-xs" value={received[i.product_id] ?? ""} onChange={(e) => setReceived({ ...received, [i.product_id]: e.target.value })} data-testid={`receive-qty-${i.product_id}`} />
                                                        ) : (i.received_qty != null ? fmtQty4(i.received_qty, i.unit) : "—")}
                                                    </td>
                                                    <td className={`p-2 font-semibold ${variance ? (variance > 0 ? "text-emerald-700" : "text-red-600") : "text-muted-foreground"}`}>
                                                        {variance != null ? (variance > 0 ? `+${fmtQty4(variance, i.unit)}` : fmtQty4(variance, i.unit)) : "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex items-center gap-2">
                                <Input placeholder="Barkod okut veya yaz..." value={scanCode} onChange={(e) => setScanCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} className="h-9 font-mono text-sm" data-testid="barcode-scan-input" />
                                <Button size="sm" variant="outline" onClick={scan} data-testid="barcode-scan-button"><ScanLine className="mr-1 h-3.5 w-3.5" /> Tara</Button>
                            </div>
                            {scanResult && (
                                <div className={`rounded-lg border p-3 text-sm ${scanResult.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="scan-result">
                                    {scanResult.ok
                                        ? <span className="font-medium text-emerald-800">✓ {scanResult.data.urun.product_name} — {scanResult.data.siparis_no} / {scanResult.data.tedarikci} → {scanResult.data.alici}</span>
                                        : <span className="font-medium text-red-700">{scanResult.msg}</span>}
                                </div>
                            )}
                            {isBuyer && detail.status === "shipped" && (
                                <DialogFooter>
                                    <Button onClick={confirmReceive} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="receive-confirm-button">
                                        {busy ? "Kaydediliyor..." : "Teslimatı Onayla"}
                                    </Button>
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
